import React, { useState, useEffect } from 'react';
import { useCompetencias } from 'hooks/useCompetencias';
import { catalogoService } from 'services/usuarioService';
import { CompetenciaResponse, CompetenciaPayload } from 'types/competencia.types';
import { Curso } from 'types/usuario.types';

// ============================================================
// CompetenciasConfigPage — Configuración de competencias por curso
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const FORM_VACIO: Omit<CompetenciaPayload, 'cursoId'> = {
  nombre: '',
  descripcion: '',
  orden: 1,
};

interface CompetenciaFilaProps {
  competencia: CompetenciaResponse;
  editando: boolean;
  saving: boolean;
  editForm: Omit<CompetenciaPayload, 'cursoId'>;
  onEditForm: (f: Omit<CompetenciaPayload, 'cursoId'>) => void;
  onAbrirEditar: (c: CompetenciaResponse) => void;
  onGuardar: () => void;
  onCancelarEdicion: () => void;
  onEliminar: () => void;
}

const CompetenciaFila: React.FC<CompetenciaFilaProps> = ({
  competencia, editando, saving, editForm, onEditForm,
  onAbrirEditar, onGuardar, onCancelarEdicion, onEliminar,
}) => {
  if (editando) {
    return (
      <tr className="bg-blue-50">
        <td className="px-4 py-2">
          <input type="number" min={1} value={editForm.orden}
            onChange={e => onEditForm({ ...editForm, orden: parseInt(e.target.value) || 1 })}
            className="w-16 px-2 py-1.5 rounded-lg border border-blue-300 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500" />
        </td>
        <td className="px-4 py-2">
          <input type="text" value={editForm.nombre}
            onChange={e => onEditForm({ ...editForm, nombre: e.target.value })}
            className="w-full px-2 py-1.5 rounded-lg border border-blue-300 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500" />
        </td>
        <td className="px-4 py-2">
          <input type="text" value={editForm.descripcion ?? ''}
            onChange={e => onEditForm({ ...editForm, descripcion: e.target.value })}
            className="w-full px-2 py-1.5 rounded-lg border border-blue-300 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500" />
        </td>
        <td className="px-4 py-2">
          <div className="flex gap-1.5">
            <button onClick={onGuardar} disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-60">
              {saving && <span className="w-3 h-3 animate-spin border border-white border-t-transparent rounded-full inline-block" />}
              Guardar
            </button>
            <button onClick={onCancelarEdicion}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 transition-colors">
              Cancelar
            </button>
          </div>
        </td>
      </tr>
    );
  }
  return (
    <tr className="hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3 text-sm font-bold text-slate-600 w-16">{competencia.orden}</td>
      <td className="px-4 py-3 text-sm font-semibold text-slate-800">{competencia.nombre}</td>
      <td className="px-4 py-3 text-sm text-slate-500">{competencia.descripcion || '—'}</td>
      <td className="px-4 py-3">
        <div className="flex gap-1.5">
          <button onClick={() => onAbrirEditar(competencia)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors">
            Editar
          </button>
          <button onClick={onEliminar}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 hover:bg-red-200 text-red-700 transition-colors">
            Eliminar
          </button>
        </div>
      </td>
    </tr>
  );
};

const CompetenciasConfigPage: React.FC = () => {
  const [cursos, setCursos] = useState<Curso[]>([]);
  const [cursoId, setCursoId] = useState<number | null>(null);
  const [cargandoCursos, setCargandoCursos] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState<CompetenciaResponse | null>(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const { competencias, loading, error, saving, saveError, crear, actualizar, eliminar } =
    useCompetencias(cursoId);

  useEffect(() => {
    const cargar = async () => {
      setCargandoCursos(true);
      try {
        const data = await catalogoService.getCursos();
        setCursos(data);
        if (data.length > 0) setCursoId(data[0].idCurso);
      } finally {
        setCargandoCursos(false);
      }
    };
    cargar();
  }, []);

  const abrirAgregar = () => {
    setForm({ ...FORM_VACIO, orden: competencias.length + 1 });
    setEditando(null);
    setShowForm(true);
  };

  const abrirEditar = (c: CompetenciaResponse) => {
    setForm({ nombre: c.nombre, descripcion: c.descripcion ?? '', orden: c.orden });
    setEditando(c);
    setShowForm(true);
  };

  const handleGuardar = async () => {
    if (!cursoId) return;
    const payload: CompetenciaPayload = { ...form, cursoId };
    if (editando) {
      await actualizar(editando.idCompetencia, payload);
    } else {
      await crear(payload);
    }
    setShowForm(false);
    setEditando(null);
  };

  const handleEliminar = async (id: number) => {
    await eliminar(id);
    setConfirmDelete(null);
  };

  if (cargandoCursos) {
    return <div className="flex justify-center py-20"><SpinnerIcon /></div>;
  }

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Competencias por Curso</h1>
        <p className="text-blue-200 mt-1">Configura las competencias evaluables de cada área curricular</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Curso / Área Curricular</label>
            <select
              value={cursoId ?? ''}
              onChange={e => { setCursoId(Number(e.target.value)); setShowForm(false); setEditando(null); }}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              {cursos.map(c => (
                <option key={c.idCurso} value={c.idCurso}>{c.nombre}</option>
              ))}
            </select>
          </div>
          <button onClick={abrirAgregar} disabled={!cursoId}
            className="bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl px-5 py-2.5 transition-colors disabled:opacity-50">
            + Agregar Competencia
          </button>
        </div>
      </div>

      {(error || saveError) && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
          {error || saveError}
        </div>
      )}

      {showForm && !editando && (
        <div className="bg-white rounded-2xl border border-blue-200 shadow-sm p-6 space-y-4">
          <h3 className="font-bold text-slate-800">Nueva competencia</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nombre</label>
              <input type="text" value={form.nombre}
                onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Comprensión lectora" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Orden</label>
              <input type="number" min={1} value={form.orden}
                onChange={e => setForm(p => ({ ...p, orden: parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="sm:col-span-3">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Descripción (opcional)</label>
              <input type="text" value={form.descripcion ?? ''}
                onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Descripción breve de la competencia..." />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditando(null); }}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
              Cancelar
            </button>
            <button onClick={handleGuardar} disabled={saving || !form.nombre.trim()}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors disabled:opacity-60">
              {saving && <SpinnerIcon />}
              Guardar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><SpinnerIcon /></div>
        ) : competencias.length === 0 ? (
          <div className="py-14 text-center">
            <p className="text-slate-400 text-sm">
              {cursoId ? 'No hay competencias configuradas para este curso' : 'Selecciona un curso para ver sus competencias'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {['Orden', 'Nombre', 'Descripción', 'Acciones'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {competencias.map(c => (
                <CompetenciaFila
                  key={c.idCompetencia}
                  competencia={c}
                  editando={editando?.idCompetencia === c.idCompetencia && showForm}
                  saving={saving}
                  editForm={form}
                  onEditForm={setForm}
                  onAbrirEditar={abrirEditar}
                  onGuardar={handleGuardar}
                  onCancelarEdicion={() => { setShowForm(false); setEditando(null); }}
                  onEliminar={() => setConfirmDelete(c.idCompetencia)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmDelete !== null && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-lg text-slate-800 mb-2">Eliminar competencia</h3>
            <p className="text-slate-500 text-sm mb-5">Esta acción no se puede deshacer.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
                Cancelar
              </button>
              <button onClick={() => handleEliminar(confirmDelete)} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60">
                {saving && <SpinnerIcon />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetenciasConfigPage;
